import { Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { membersAppSelectors } from 'redux/slices/memberSlice';
import { memberStatusChangeRequestAppSelectors } from 'redux/slices/memberStatusChangeRequestSlice';

import { FeedItemDto } from '@act/shared/data-transfer-objects';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import useMemberStatusChangeRequestDialog from 'react-components/dialog-forms/hooks/use-member-status-change-request-dialog';
import { actingUserAppSelectors } from 'redux/slices/actingUserSlice';
import { userAppSelectors } from 'redux/slices/usersSlice';
import InfoCard, {
  AdditionalFeedCardActions,
} from 'toolkit/info-card/info-card';
import { TaggableChip, ContentPreview } from '@act/storybook';
import { timeFormatter } from '@act/shared/utilities';
import { Permission } from '@act/shared/authorization';

interface MemberStatusChangeRequestFeedCardProps {
  feedItem: FeedItemDto;
  memberStatusChangeRequestId: number;
  shouldHighlight: boolean;
}

const MemberStatusChangeRequestFeedCard = ({
  memberStatusChangeRequestId,
  feedItem,
  shouldHighlight,
}: MemberStatusChangeRequestFeedCardProps) => {
  const menuItems: AdditionalFeedCardActions[] = [];

  const openMemberStatusChangeRequestDialog =
    useMemberStatusChangeRequestDialog();

  const memberStatusChangeRequest =
    memberStatusChangeRequestAppSelectors.useSelectMemberStatusChangeRequestById(
      memberStatusChangeRequestId
    );

  const author = userAppSelectors.useSelectUserById(
    memberStatusChangeRequest?.creatorId
  );
  const memberStatus = membersAppSelectors.useSelectMemberStatusById(
    memberStatusChangeRequest?.memberStatusId
  );
  const member = membersAppSelectors.useSelectMemberById(
    memberStatusChangeRequest?.memberId
  );
  const actingUser = actingUserAppSelectors.useSelectActingUser();

  const canEditCompletedRequest = actingUserAppSelectors.useUserHasPermission(
    Permission.CAN_ALTER_COMPLETE_MEMBER_STATUS_REQUESTS
  );
  const canAuditStatusChangeRequest =
    membersAppSelectors.useHasPermissionOnMember(
      member?.id,
      Permission.CAN_AUDIT_MEMBER_STATUS_CHANGE_REQUESTS
    );

  if (!memberStatusChangeRequest) {
    return (
      <>
        An Error occurred. Member's status change request was not found or was
        deleted.
      </>
    );
  }

  const isAuthor = memberStatusChangeRequest.creatorId === actingUser.id;

  const isPending = !(
    !!memberStatusChangeRequest.approvedAt ||
    !!memberStatusChangeRequest.deniedAt
  );

  if (
    (isAuthor || canAuditStatusChangeRequest) &&
    (isPending || canEditCompletedRequest)
  ) {
    let editText = 'Edit Status Change';
    if (canAuditStatusChangeRequest) {
      editText = 'Review Status Change';
    }
    menuItems.push({
      title: editText,
      details: editText,
      handler: () => {
        void openMemberStatusChangeRequestDialog({
          memberId: member.id,
          memberStatusChangeRequest: memberStatusChangeRequest,
          isEdit: isAuthor && isPending,
        });
      },
    });
  }

  let content;
  let chip: React.ReactElement;
  if (feedItem.action == 'audited') {
    if (memberStatusChangeRequest?.auditorComment) {
      content = (
        <>
          <Typography variant="subheadMedium">Auditor Comment:</Typography>
          <br />
          <Typography variant="bodyLarge">
            {memberStatusChangeRequest.auditorComment}
          </Typography>
        </>
      );
    }

    if (memberStatusChangeRequest.approvedAt) {
      chip = <TaggableChip isTag color="success" label="Approved" />;
    }

    if (memberStatusChangeRequest.deniedAt) {
      chip = (
        <TaggableChip
          isTag
          icon={<RemoveCircleIcon />}
          color="error"
          label="Declined"
        />
      );
    }
  } else if (feedItem.action === 'scheduled') {
    if (DateTime.fromMillis(feedItem.timestamp) > DateTime.now())
      chip = <TaggableChip label="Scheduled" />;
  } else if (feedItem.action === 'created') {
    if (memberStatusChangeRequest.creatorComment) {
      content = (
        <>
          <Typography variant="subheadMedium">Comment:</Typography>
          <br />
          <Typography variant="bodyLarge">
            {memberStatusChangeRequest.creatorComment}
          </Typography>
        </>
      );
    }
  }

  const expandedContent = (
    <ContentPreview>
      <div className="info-pane">
        {memberStatus && (
          <>
            <h6>Reason for Change Request</h6>
            <p>{memberStatus.name}</p>
          </>
        )}
        <h6>Effective At</h6>
        <p>
          {timeFormatter.epochToHumanReadableDate({
            date: memberStatusChangeRequest.effectiveAt,
          })}
        </p>
        <h6>Requesting Guide</h6>
        <p>{author?.name}</p>
        {memberStatusChangeRequest.creatorComment && (
          <>
            <h6>Comments</h6>
            {memberStatusChangeRequest.creatorComment}
          </>
        )}
        {memberStatusChangeRequest.auditorComment && (
          <>
            <h6>Auditor Comment</h6>
            {memberStatusChangeRequest.auditorComment}
          </>
        )}
      </div>
    </ContentPreview>
  );

  return (
    <InfoCard
      variant="change"
      startTime={feedItem.createdAt}
      subject="Status Change Request"
      menuItems={menuItems}
      statusChips={[chip]}
      shouldHighlight={shouldHighlight}
    >
      <>
        <Typography display="block" variant="labelLarge" component="p">
          {memberStatus?.key === 'off-plan'
            ? 'Member going off-plan'
            : memberStatus?.name}
        </Typography>
        {content}
        {expandedContent}
      </>
    </InfoCard>
  );
};

export default MemberStatusChangeRequestFeedCard;
